import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';
import * as Yup from 'yup';
import { v4 } from 'uuid';
import { doc, setDoc, updateDoc } from 'firebase/firestore';
import { Modal } from '../Modal';
import { useAuth } from '~/hooks/auth';
import { DB } from '~/services/firebase';
import { useToast } from '~/hooks/toast';
import getValidationErrors from '~/utils/getValidationErrors';
import { bff } from '~/services/bff';

export interface OpenCashModalPropsRef {
  open: () => void;
}

interface OpenCashModalProps {
  isOpen: boolean;
}

const { REACT_APP_FIREBASE_PROJECT } = process.env;

const OpenCashModal: React.RefForwardingComponent<
  OpenCashModalPropsRef,
  OpenCashModalProps
> = ({ isOpen }, ref) => {
  const { user, company, loadCompany } = useAuth();
  const { addToast } = useToast();

  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({} as any);

  useImperativeHandle(ref, () => ({
    open: () => {
      setShow(true);

      setTimeout(() => {
        ($('.money') as any).mask('#.##0,00', { reverse: true });
      }, 1000);
    },
  }));

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      try {
        let initValue = (document.getElementById('initvalue') as any)
          .value as string;
        initValue = initValue.replaceAll('.', '');
        initValue = initValue.replaceAll(',', '.');

        const data = {
          name: isOpen ? 'Saldo Final' : 'Saldo Inicial',
          date: new Date(),
          value: initValue,
          type: isOpen ? '99' : '100',
        };

        const schema = Yup.object().shape({
          name: Yup.string().required('O nome do lançamento é obrigatório'),
          date: Yup.string().required('A data do lançamento é obrigatória'),
          value: Yup.number().required('O valor do lançamento é obrigatório'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        setLoading(true);

        const { name, date, value } = data;

        const parseDate = new Date(date);

        const uid = v4();

        await setDoc(doc(DB, 'cashflow', uid), {
          ...data,
          uid,
          name,
          date: parseDate,
          value,
          user_id: user.uid,
          company_id: company.uid,
        });

        await updateDoc(doc(DB, 'companies', company.uid), {
          cashOpenedAt: isOpen ? null : new Date(),
        });

        await bff.delete(`/companies/${REACT_APP_FIREBASE_PROJECT}`);
        await loadCompany();

        addToast({
          type: 'success',
          description: isOpen
            ? 'Caixa encerrado com sucesso!'
            : 'Caixa aberto com sucesso!',
        });

        setLoading(false);
        setShow(false);
      } catch (err: any) {
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const parseErrors = getValidationErrors(err);
          setErrors(parseErrors);
        }
      }
    },
    [user, company, addToast, isOpen, loadCompany],
  );

  return (
    <>
      {show && (
        <Modal
          title={isOpen ? 'Fechar Caixa' : 'Abrir Caixa'}
          size="sm"
          show={show}
          onClose={() => setShow(false)}
        >
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">
                {isOpen ? 'Saldo Final' : 'Saldo Inicial'}
              </label>
              <div className="form-control-wrap">
                <input
                  id="initvalue"
                  type="text"
                  className="form-control money"
                />
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-lg btn-primary"
              disabled={loading}
            >
              {loading && (
                <div
                  className="spinner-border spinner-border-sm"
                  role="status"
                />
              )}
              {!loading && (isOpen ? 'Fechar Caixa' : 'Abrir Caixa')}
            </button>
          </form>
        </Modal>
      )}
    </>
  );
};

export default forwardRef(OpenCashModal);
